import React, { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  FlexRow,
  TablePaginatedV8,
  order2Sorting,
  toastSuccess,
  toastWarning,
  promiseModalDialog,
  usePaginatedQueryV8,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useTranslation } from 'react-i18next';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import {
  gqlDeleteNotificationRecipient,
  gqlGetCollectionpointEmailForNotification,
  gqlGetNotificationRecipients,
  gqlResendNotification,
  gqlUpdateNotificationRecipient,
} from '../graphql';
import {
  formatResponsePayload,
  NotificationRecipientStatus,
  notificationResponseValueLabel,
} from '../constants';
import { NotificationRecipientStatusBadge } from '../notification-recipient-status-badge';
import { getRouteByName } from 'app/routes';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { FaEnvelope, FaRedo, FaSync } from 'react-icons/fa';
import { formatDateTime } from 'shared-pages/utils/helpers';
import { handleExport } from './export-columns';
import { RecipientsFilters, buildRecipientsWhereFromFilterValues } from './recipients-filters';

function hasRecipientViewFilters(values): boolean {
  if (!values) return false;
  if (values.searchText) return true;
  if (values.statusId !== undefined && values.statusId !== null && values.statusId !== '') {
    return true;
  }
  return false;
}

export const RecipientsGrid = ({
  notificationId,
  canRemove,
  canResend,
  refreshKey,
  onCountChange,
  onResendableCountChange,
  onResent,
  headerActions,
}: {
  notificationId: number;
  canRemove: boolean;
  canResend?: boolean;
  refreshKey?: number;
  onCountChange?: (count: number) => void;
  onResendableCountChange?: (count: number) => void;
  onResent?: () => void;
  /** Ενέργειες δίπλα στον τίτλο, π.χ. προσθήκη σημείων όσο η ενημέρωση είναι προσχέδιο */
  headerActions?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { appStore, tableStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('RecipientsGrid');
  const location = useLocation<any>();
  const client = useApolloClient();

  const query = usePaginatedQueryV8(
    gqlGetNotificationRecipients,
    buildRecipientsWhereFromFilterValues,
    'id ASC',
    appStore,
    true,
    {},
    false,
    'notif_recipients_grid',
    { notificationId },
  );

  const {
    loading,
    data,
    dataCount,
    error,
    initPageIndex,
    gridPageChanged,
    filtersChanged,
    queryState,
    queryParams,
    refetch,
  } = query;

  React.useEffect(() => {
    if (refreshKey != null) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  React.useEffect(() => {
    if (dataCount == null) return;
    // Το πλήθος για αποστολή είναι το σύνολο, όχι ό,τι δείχνει το φίλτρο
    if (!hasRecipientViewFilters(queryState?.filterValues)) {
      onCountChange?.(dataCount);
    }
    const resendable = (data ?? []).filter(
      (row: any) =>
        row.statusId === NotificationRecipientStatus.Failed ||
        row.statusId === NotificationRecipientStatus.NoEmail,
    ).length;
    onResendableCountChange?.(resendable);
  }, [data, dataCount, onCountChange, onResendableCountChange, queryState?.filterValues]);

  const [deleteRecipient] = useMutation(gqlDeleteNotificationRecipient);
  const [resendRecipient, { loading: resending }] = useMutation(gqlResendNotification);
  const [updateRecipient] = useMutation(gqlUpdateNotificationRecipient);
  const [refreshingRecipientId, setRefreshingRecipientId] = React.useState<number | null>(null);

  /**
   * Σε προσχέδιο το email είναι στιγμιότυπο. Αν διορθώθηκε στην καρτέλα του σημείου,
   * το ξαναδιαβάζουμε και ενημερώνουμε κατάσταση (Χωρίς email → Σε ουρά).
   */
  const handleRefreshEmail = async (row: any) => {
    setRefreshingRecipientId(row.id);
    try {
      const cpRes = await client.query({
        query: gqlGetCollectionpointEmailForNotification,
        variables: { id: row.collectionpointId },
        fetchPolicy: 'network-only',
      });
      const liveEmail = String(cpRes.data?.getCollectionpointById?.email ?? '').trim() || null;
      await updateRecipient({
        variables: {
          data: {
            id: row.id,
            notificationId: row.notificationId,
            collectionpointId: row.collectionpointId,
            email: liveEmail,
            statusId: liveEmail
              ? NotificationRecipientStatus.Queued
              : NotificationRecipientStatus.NoEmail,
          },
        },
      });
      if (liveEmail) {
        toastSuccess(t('Ενημερώθηκε το email από την καρτέλα του σημείου'));
      } else {
        toastWarning(t('Το σημείο συλλογής δεν έχει ακόμα email'));
      }
      refetch();
    } catch (error: any) {
      toastWarning(error?.message ?? t('Αποτυχία ανανέωσης email'));
    } finally {
      setRefreshingRecipientId(null);
    }
  };

  const handleRowResend = async (recipientId: number) => {
    const confirmed = await promiseModalDialog({
      title: t('Επαναποστολή'),
      text: t(
        'Θα σταλεί ξανά το mail σε αυτόν τον παραλήπτη. Αν δεν έχει email στο σημείο συλλογής, δεν θα σταλεί.',
      ),
    });
    if (!confirmed) return;
    try {
      const { data: result } = await resendRecipient({
        variables: { id: notificationId, recipientId },
      });
      if (!result?.resendNotification?.id) throw new Error(t('Αποτυχία επαναποστολής'));
      toastSuccess(t('Ο παραλήπτης μπήκε ξανά στην ουρά αποστολής'));
      refetch();
      onResent?.();
    } catch (error: any) {
      toastWarning(error?.message ?? t('Αποτυχία επαναποστολής'));
    }
  };

  const hasResendableRow = (data ?? []).some(
    (row: any) =>
      row.statusId === NotificationRecipientStatus.Failed ||
      row.statusId === NotificationRecipientStatus.NoEmail,
  );

  const columns = useMemo(
    () => [
      {
        id: 'collectionpoint.name',
        header: t('Σημείο συλλογής'),
        accessorKey: 'collectionpoint.name',
        cell: ({ row }) => {
          function printIfNotEmpty(value: string) {
            if (value) return <div className="text-muted small">{value}</div>;
            return null;
          }
          const value = row.original.collectionpoint?.id;
          const path =
            value !== undefined && value !== null && value !== '' && value !== 'unknown'
              ? generatePath(getRouteByName('Collectionpoint'), { id: value })
              : '';
          const cpName =
            value && path ? (
              <Link to={path}>{row.original.collectionpoint?.name}</Link>
            ) : (
              row.original.collectionpoint?.name
            );
          return (
            <div>
              {cpName}
              {printIfNotEmpty(row.original.collectionpoint?.addressLine1)}
              {printIfNotEmpty(row.original.collectionpoint?.addressLine2)}
              {printIfNotEmpty(row.original.collectionpoint?.addressArea)}
              {printIfNotEmpty(row.original.collectionpoint?.addressCity)}
              {printIfNotEmpty(row.original.collectionpoint?.addressNomos)}
            </div>
          );
        },
        size: 24,
      },
      {
        id: 'email',
        header: t('Email'),
        accessorKey: 'email',
        cell: ({ row }) => {
          const email = row.original.email;
          // Το mail δημιουργείται στην ουρά (mailsrepo) μόνο μετά την αποστολή της ενημέρωσης
          const mailsrepoId = row.original.mailsrepoId;
          const path =
            mailsrepoId != null && mailsrepoId !== ''
              ? generatePath(getRouteByName('Mail'), { id: mailsrepoId })
              : '';
          const showRefresh = canRemove && !mailsrepoId;
          return (
            <div>
              <div className="d-flex align-items-center gap-1">
                <span>{email}</span>
                {showRefresh ? (
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    disabled={refreshingRecipientId === row.original.id}
                    title={t('Ανανέωση email από την καρτέλα του σημείου')}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleRefreshEmail(row.original);
                    }}
                  >
                    <FaSync />
                  </Button>
                ) : null}
              </div>
              {path ? (
                <div>
                  <Link
                    to={{
                      pathname: path,
                      state: { pathname: location.pathname, ...location.state },
                    }}
                    title={t('Προβολή απεσταλμένου email')}
                  >
                    <FaEnvelope /> προβολή
                  </Link>
                </div>
              ) : null}
            </div>
          );
        },
        size: 12,
      },
      {
        id: 'statusId',
        header: t('Κατάσταση'),
        accessorKey: 'statusId',
        cell: ({ row }) => {
          return (
            <div className="d-flex align-items-center gap-1">
              <NotificationRecipientStatusBadge statusId={row.original.statusId} />
            </div>
          );
        },
        size: 14,
      },
      {
        id: 'responseValue',
        header: t('Απάντηση'),
        accessorKey: 'responseValue',
        cell: ({ row }) => {
          const responseValue = row.original.responseValue;
          const respondedDate = row.original.respondedDate;
          const payloadText = formatResponsePayload(row.original.responsePayload);
          return (
            <div>
              <div>{t(notificationResponseValueLabel(responseValue))}</div>
              {respondedDate ? (
                <div className="text-muted small">{formatDateTime(respondedDate)}</div>
              ) : null}
              {payloadText ? (
                <div
                  className="text-muted small mt-1"
                  style={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto' }}
                >
                  {payloadText}
                </div>
              ) : null}
            </div>
          );
        },
        size: 22,
      },
      ...(canRemove || (canResend && hasResendableRow)
        ? [
            {
              id: 'recipient-actions',
              header: 'Ενέργειες',
              cell: ({ row }) => {
                // Σε προσχέδιο μόνο αφαίρεση — η επαναποστολή είναι μετά την αποστολή
                if (canRemove) {
                  return (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await deleteRecipient({ variables: { id: row.original.id } });
                        toastSuccess(t('Αφαιρέθηκε από το κοινό'));
                        refetch();
                      }}
                    >
                      {t('Αφαίρεση')}
                    </Button>
                  );
                }
                const statusId = row.original.statusId;
                const canRowResend =
                  statusId === NotificationRecipientStatus.Failed ||
                  statusId === NotificationRecipientStatus.NoEmail;
                if (!canResend || !canRowResend) return null;
                return (
                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={resending}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleRowResend(row.original.id);
                    }}
                  >
                    <FaRedo className="me-1" />
                    {t('Επαναποστολή')}
                  </Button>
                );
              },
              size: 12,
            },
          ]
        : []),
    ],
    [
      t,
      canRemove,
      canResend,
      hasResendableRow,
      deleteRecipient,
      refetch,
      location.pathname,
      location.state,
      resending,
      handleRowResend,
      handleRefreshEmail,
      refreshingRecipientId,
    ],
  );

  const tableRef = useRef(null);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  return (
    <div className="mt-3">
      <FlexRow className="mb-2 align-items-center">
        <h5 className="m-0">
          {t('Παραλήπτες ενημέρωσης')}
          {dataCount != null ? ` (${dataCount})` : ''}
        </h5>
        {headerActions}
      </FlexRow>
      <RecipientsFilters onChange={filtersChanged} initialValues={queryState?.filterValues ?? {}} />
      <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
        {data ? (
          <TablePaginatedV8
            ref={tableRef}
            loading={loading}
            id="notif-recipients"
            key={`notif-recipients-${canRemove ? 'remove' : canResend ? 'resend' : 'view'}`}
            appStore={appStore}
            tableStore={tableStore}
            columns={columns as any}
            data={data}
            dataCount={dataCount}
            onPageChange={gridPageChanged}
            initPageIndex={initPageIndex}
            initSorting={order2Sorting('id ASC')}
            onExport={() => {
              handleExport(client, queryParams?.where, t);
            }}
            topBorderRadius={false}
          />
        ) : (
          <div style={{ height: '120px' }} />
        )}
      </LoadingOverlay>
    </div>
  );
};
